import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Linking, Platform, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Clipboard from '@react-native-clipboard/clipboard';
import { Query } from '@/utils/react-query/Queries';
import { getLocalPaymentMethods, saveLocalPaymentMethod, calculateGivingCharge } from '@/data/giving';
import { formPaymentRequest, formatPaymentAmount, getFormPaymentLink, paymentStatusLabels } from '@/data/formPayments';
import PaymentMethodPicker from '@/components/Giving/PaymentMethodPicker';
import AddPaymentMethodModal from '@/components/Giving/AddPaymentMethodModal';
import FeeCoverageToggle from '@/components/Giving/FeeCoverageToggle';
import SolidButton from '@/components/General/SolidButton';
import Input from '@/components/General/inputs/Input';
import Callout from '@/components/General/Callout';
import { isPaymentMethodUsable, preferredPaymentMethodID } from '@/utils/paymentMethodStatus';

export default function FormPaymentCheckout({ token, context: initialContext, snapshot, initialSelection, prepare = false, onChange, autoStart = false, onPaymentSubmitted }) {
    const [context, setContext] = useState(initialContext);
    const [payment, setPayment] = useState(null);
    const [localMethods, setLocalMethods] = useState([]);
    const [selectedID, setSelectedID] = useState(initialSelection?.paymentMethodID || null);
    const [coverFees, setCoverFees] = useState(initialSelection?.coverFees !== false);
    const [name, setName] = useState(initialSelection?.billingContact?.name || '');
    const [email, setEmail] = useState(initialSelection?.billingContact?.email || '');
    const [postal, setPostal] = useState(initialSelection?.billingContact?.postal_code || '');
    const [showAdd, setShowAdd] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [serverQuote, setServerQuote] = useState(null);
    const [copied, setCopied] = useState(false);
    const paying = useRef(false);
    const autoStarted = useRef(false);
    const changeRef = useRef(onChange);
    changeRef.current = onChange;
    const { data: user } = Query.people.loggedInUser();
    const ownOrg = !!(user?.id && user.organizationID === context?.organizationID);
    const { data: dbMethods } = Query.giving.paymentMethods({
        organizationID: context?.organizationID, personID: user?.id, options: { enabled: ownOrg },
    });
    useEffect(() => { if (initialContext) setContext(initialContext); }, [initialContext]);
    useEffect(() => {
        if (!context?.orgHandle) return;
        let active = true;
        getLocalPaymentMethods(context.orgHandle).then((methods) => { if (active) setLocalMethods(methods); });
        return () => { active = false; };
    }, [context?.orgHandle]);
    useEffect(() => {
        if (!ownOrg) return;
        setName((value) => value || `${user.firstName || ''} ${user.lastName || ''}`.trim());
        setEmail((value) => value || user.email || '');
    }, [ownOrg, user]);
    const methods = useMemo(() => {
        const unique = new Map();
        for (const method of [...(ownOrg ? dbMethods || [] : []), ...localMethods]) {
            const id = method.paymentMethodID || method.id;
            if (id && !unique.has(id)) unique.set(id, method);
        }
        return [...unique.values()];
    }, [ownOrg, dbMethods, localMethods]);
    useEffect(() => {
        if (!methods.length) return;
        const nextID = preferredPaymentMethodID(methods, selectedID);
        if (nextID !== selectedID) { setSelectedID(nextID); setServerQuote(null); }
    }, [methods, selectedID]);
    const selected = methods.find((method) => (method.paymentMethodID || method.id) === selectedID);
    const actualSnapshot = payment?.snapshot || snapshot;
    const actualCover = !context?.allowGiversToChooseCoverFees || coverFees;
    const calculated = calculateGivingCharge(actualSnapshot?.amountCents || 0, selected, context?.feeSummary, actualCover);
    const quote = isPaymentMethodUsable(selected) ? serverQuote || calculated : null;
    const billingContact = useMemo(() => ({ name, email, ...(postal ? { postal_code: postal } : {}) }), [name, email, postal]);
    useEffect(() => {
        changeRef.current?.({ paymentMethodID: isPaymentMethodUsable(selected) ? selectedID : null, coverFees: actualCover, billingContact, expectedGrossAmount: quote?.grossAmount });
    }, [selectedID, selected, actualCover, billingContact, quote?.grossAmount]);
    const refresh = useCallback(async () => {
        if (!token) return null;
        const result = await formPaymentRequest({ token, action: 'status' });
        setPayment(result);
        setContext(result.context);
        return result;
    }, [token]);
    useEffect(() => {
        if (prepare || !token) return;
        refresh().catch((err) => setError(err.message));
    }, [prepare, token, refresh]);
    useEffect(() => {
        if (payment?.status !== 'PENDING') return;
        let count = 0;
        const timer = setInterval(() => {
            if (++count > 12) return clearInterval(timer);
            refresh().catch((err) => setError(err.message));
        }, 10000);
        return () => clearInterval(timer);
    }, [payment?.status, refresh]);
    const pay = useCallback(async (expectedTotal) => {
        if (paying.current || !token || !selectedID || !quote || !name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
        paying.current = true;
        setBusy(true);
        setError(null);
        try {
            const result = await formPaymentRequest({ token, action: 'pay', paymentMethodID: selectedID, coverFees: actualCover, billingContact, expectedGrossAmount: Number.isInteger(expectedTotal) ? expectedTotal : quote.grossAmount });
            if (result.confirmationRequired) {
                setServerQuote(result);
                setError('Payment total changed. Review the total and press Pay to confirm.');
                return;
            }
            const current = await refresh();
            if (['PAID', 'PENDING'].includes(current?.status)) onPaymentSubmitted?.(current);
        } catch (err) {
            setError(err.message);
            try { await refresh(); } catch { setError(err.message); }
        } finally {
            paying.current = false;
            setBusy(false);
        }
    }, [token, selectedID, quote, name, email, actualCover, billingContact, refresh, onPaymentSubmitted]);
    useEffect(() => {
        if (!autoStart || autoStarted.current || payment?.status !== 'UNPAID' || !initialSelection?.paymentMethodID || selectedID !== initialSelection.paymentMethodID || !isPaymentMethodUsable(selected) || !quote || !name || !email) return;
        autoStarted.current = true;
        pay(initialSelection.expectedGrossAmount);
    }, [autoStart, payment?.status, initialSelection, selectedID, selected, quote, name, email, pay]);
    const handleAdded = async (method) => {
        await saveLocalPaymentMethod(context.orgHandle, method);
        setLocalMethods((current) => [method, ...current.filter((item) => item.paymentMethodID !== method.paymentMethodID)]);
        setSelectedID(method.paymentMethodID);
        setServerQuote(null);
    };
    const canPay = !payment?.archived && context?.electronicEnabled && ['electronic', 'both'].includes(actualSnapshot?.allowedChannels);
    const settled = ['PAID', 'NOT_REQUIRED'].includes(payment?.status);
    if (!prepare && !payment) return <View style={styles.container}>{error ? <Callout type="warning" message={error} /> : <ActivityIndicator />}<SolidButton text="Check payment status" onPress={() => refresh().catch((err) => setError(err.message))} type="forms" /></View>;
    return (
        <View style={styles.container} testID="form-payment-checkout">
            {!prepare && <Text style={styles.heading}>{paymentStatusLabels[payment?.status] || 'Payment'}</Text>}
            {settled ? (
                <Callout type="success" message={payment.status === 'PAID' ? `Payment confirmed: ${formatPaymentAmount(payment.chargedAmountCents ?? actualSnapshot.amountCents)} total, including ${formatPaymentAmount(payment.feeCents)} fee coverage.` : 'No payment is required for this entry.'} />
            ) : payment?.status === 'PENDING' && !payment.retryAvailable ? (
                <View style={styles.container}>
                    <Callout type="warning" message="Payment is pending. ACH can take several days. Do not submit another payment while this one is processing." />
                    <SolidButton text="Check payment status" type="forms" onPress={() => refresh().catch((err) => setError(err.message))} />
                </View>
            ) : canPay ? (
                <View style={styles.container}>
                    {payment?.retryAvailable && <Callout type="warning" message="An earlier attempt was interrupted. Resume with the original payment method; payment terms remain unchanged while its outcome is unknown." />}
                    <Input title="Payer name" value={name} onUpdate={setName} disabled={busy} testID="form-payment-payer-name" />
                    <Input title="Payer email" value={email} onUpdate={setEmail} disabled={busy} testID="form-payment-payer-email" />
                    <Input title="Billing postal code" value={postal} onUpdate={setPostal} disabled={busy} testID="form-payment-postal" />
                    <PaymentMethodPicker methods={methods} selectedPaymentMethodID={selectedID} onSelect={busy ? undefined : (id) => { setSelectedID(id); setServerQuote(null); }} onAdd={busy ? undefined : () => setShowAdd(true)} testID="form-payment-method" />
                    <FeeCoverageToggle allowed={context.allowGiversToChooseCoverFees} value={coverFees} onChange={(value) => { setCoverFees(value); setServerQuote(null); }} disabled={busy} />
                    <Text style={styles.total}>Price {formatPaymentAmount(actualSnapshot.amountCents)} · Fee coverage {formatPaymentAmount(quote?.convenienceFee)} · Total {formatPaymentAmount(quote?.grossAmount ?? actualSnapshot.amountCents)}</Text>
                    {!prepare && <SolidButton text={`Pay ${formatPaymentAmount(quote?.grossAmount ?? actualSnapshot.amountCents)}`} onPress={pay} processing={busy} disabled={!selectedID || !quote || !name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)} type="forms" testID="form-payment-pay" />}
                </View>
            ) : <Callout type="warning" message={payment?.archived ? 'This entry is archived and cannot accept payments.' : 'Electronic payment is unavailable. An entry manager can record an allowed in-person payment.'} />}
            {error && <Callout type="warning" message={error} />}
            {!prepare && token && !settled && <SolidButton text={copied ? 'Payment link copied' : 'Copy payment link'} type="forms" variant="outlined" onPress={() => { Clipboard.setString(getFormPaymentLink(token)); setCopied(true); }} testID="form-payment-copy-link" />}
            {!prepare && settled && payment?.redirectUrl && <SolidButton text="Continue" type="forms" onPress={() => { if (Platform.OS === 'web') window.location.assign(payment.redirectUrl); else Linking.openURL(payment.redirectUrl); }} />}
            {context && <AddPaymentMethodModal isOpen={showAdd} onClose={() => setShowAdd(false)} onPaymentMethodAdded={handleAdded} organizationID={context.organizationID} personID={user?.id} orgHandle={context.orgHandle} billingContact={billingContact} authenticated={ownOrg} />}
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: { gap: spacing.sm, width: '100%' },
    heading: { color: colors.forms, fontFamily: fonts.heading, fontSize: typography.h3 },
    total: { color: colors.text, fontFamily: fonts.body, fontSize: typography.body },
}));
